import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Toast, type ToastAction, type ToastKind } from '../primitives/Toast'
import { useRegionRotor } from '../a11y/useRegionRotor'
import { useStackFocus } from './useStackFocus'
import './toaster.css'

export type ToastRequest = {
  kind: ToastKind
  message: string
  action?: ToastAction
}

type Queued = ToastRequest & { id: string }

/** `ToastEvent`, not `Event` — that name is the DOM global for the whole file. */
type ToastEvent = { type: 'show'; toast: Queued } | { type: 'dismiss'; id: string }

/**
 * How many toasts may be on screen at once.
 *
 * **A cap, because the unbounded version failed the criterion.** Eight errors
 * at 375x667 grew the stack to 632px of a 667px viewport, covering the app for
 * the full eight seconds — and eight insertions
 * into a live region is #194's "announcements stack into noise" whichever way
 * that clause is read. A retry loop or one failure per row of a batch reaches
 * this immediately.
 *
 * Three because the stack is bottom-anchored and a fourth row starts to eat the
 * content it is reporting on.
 */
const MAX_VISIBLE = 3

/**
 * Admitting a toast, and choosing which one leaves if the stack is full.
 *
 * **Which one goes is a policy, and two naive versions of it were wrong in
 * measurable ways.** A flat `slice(-MAX_VISIBLE)` drops by age alone: eight
 * errors followed by eight successes left three toasts, all of them "Saved."
 * That sits badly beside this file's own decision to render the assertive
 * region first *because* a failure must not be buried — and because
 * `aria-relevant` is deliberately at its default, the removal is announced to
 * nobody.
 *
 * **The ARRIVAL is never a candidate.** Searching `[...queue, toast]` meant
 * that with three errors up, the only ordinary entry was the toast that had
 * just arrived — dropped inside the same reducer call, never painted and never
 * inserted into a live region. Measured: eleven successes behind three errors
 * produced ZERO insertions. Three failed saves followed by the save that
 * finally works would report nothing at all, in the retry loop this cap exists
 * for. It also contradicts `useToast`'s own posture — *"a swallowed toast is
 * the worst of both worlds"*.
 *
 * Three rungs, over the existing queue only:
 *
 *   1. the oldest ordinary toast — not a failure, not one still offering an
 *      action the user has not taken
 *   2. failing that, the oldest NON-FAILURE, so two errors and an untaken
 *      action do not cost the user an error they have not read
 *   3. failing that, the oldest, because a bounded stack is the point
 *
 * Never more than one over the cap, so removing one is always enough.
 */
const EVICTION_ORDER: ((toast: Queued) => boolean)[] = [
  (t) => t.kind !== 'error' && t.action === undefined,
  (t) => t.kind !== 'error',
  () => true,
]

function admit(queue: Queued[], toast: Queued): Queued[] {
  if (queue.length < MAX_VISIBLE) return [...queue, toast]
  let evicted = 0
  for (const wanted of EVICTION_ORDER) {
    const found = queue.findIndex(wanted)
    if (found !== -1) {
      evicted = found
      break
    }
  }
  return [...queue.filter((_, index) => index !== evicted), toast]
}

function reduce(queue: Queued[], event: ToastEvent): Queued[] {
  switch (event.type) {
    case 'show':
      return admit(queue, event.toast)
    case 'dismiss':
      return queue.filter((toast) => toast.id !== event.id)
  }
}

/**
 * A counter, not `crypto.randomUUID()`.
 *
 * `randomUUID` is secure-context-only and throws on the plain-http LAN origin
 * this repo uses for real-device testing — a toast is the last thing that
 * should be the reason a page dies. Nothing here needs unguessability; the id
 * exists so React has a stable key and `dismiss` has something to name.
 */
let sequence = 0

type Api = { show: (request: ToastRequest) => void }

const ToastContext = createContext<Api | null>(null)

/**
 * The one toast queue.
 *
 * **Not in `primitives/`, and that is enforced rather than stylistic.**
 * `StatesReference.test.tsx` treats any capitalised function in that directory
 * as a component owing five states, so a provider dropped there is demanded by
 * the registry and produces a section documenting nothing. `Toast` — the thing
 * with a look — stays in `primitives/` and IS registered.
 *
 * **One live region per politeness level, mounted from the first render.**
 * #194's first criterion is the one with teeth: *"Announcements do not stack
 * into noise."* Three toasts must not be three competing regions, and a region
 * created at the moment it is filled is an INSERTION — the shape assistive tech
 * handles worst, which `useAnnouncement` and `Security.tsx` both already record.
 *
 * **`useAnnouncement` is deliberately NOT used here**, and the plan's
 * instruction to reuse it does not survive contact with the structure the plan
 * itself specifies. That hook solves one defect: React writing the SAME string
 * to the SAME text node is not a mutation, so a repeat is silent. A queue of
 * distinct, keyed nodes has no such node — the second identical toast is a new
 * element inserted into the region, which is a real change. `Toast.test.tsx`
 * asserts the outcome ("the same message twice is announced twice") with a
 * MutationObserver rather than trusting either mechanism.
 *
 * The invariant that reasoning rests on: **the id is a monotonic sequence.**
 * Deduping by message, or keying on it, re-introduces the exact silence
 * `useAnnouncement` exists for — same string, same node, no mutation, nothing
 * announced. If a dedupe is ever wanted, the region has to go back to that hook.
 *
 * **The keyboard route in, which used not to exist — #216.** This host is the
 * last child of `<body>`, so the queue was 27 tab stops from the top of an
 * ordinary page, and under an open `Overlay` it was unreachable outright:
 * measured, 15 and then 20 Tab presses never got there, because the modal traps
 * focus inside its panel by design. Two things fix that, and the issue's
 * decision was explicitly both rather than either.
 *
 * The queue is a NAMED LANDMARK, so assistive technology can jump straight to
 * it. `role="region"` with an `aria-label` is what makes it one — a region
 * without a name is not exposed as a landmark at all, so the label is
 * load-bearing rather than descriptive.
 *
 * And `useRegionRotor` gives every other keyboard user an F6 route. That is the
 * half the landmark cannot cover: a screen-reader user could always jump, but
 * under a dialog the trap holds for anyone driving Tab, and no clock helps —
 * `ACTIONABLE_MS` is 20s for a reason that stops applying the moment the route
 * exists. It lives here rather than in the shell because the queue is the
 * region that had no way in, and because the auth screens raise toasts with no
 * chrome around them.
 *
 * `tabIndex={-1}` because a landmark is not focusable on its own — the rotor
 * would announce nothing to land on.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  // Two providers give two hosts and four live regions — precisely what this
  // whole design exists to avoid, and `document.querySelector('.toaster')`
  // would silently pick one. Loud, matching `useToast`'s own posture.
  if (useContext(ToastContext) !== null) {
    throw new Error('<ToastProvider> is already mounted above this one — there is one queue')
  }

  const [queue, dispatch] = useReducer(reduce, [])

  /**
   * A host appended to `<body>`, carrying the attribute `Overlay` reserves.
   *
   * An open `Overlay` sets `inert` on every other child of `<body>`, and `inert`
   * removes a subtree from the ACCESSIBILITY TREE — so a live region inside it
   * is never announced. That would silently kill every toast raised while a
   * dialog is open, including the failure a `ConfirmDialog` deliberately stays
   * open to show.
   */
  const host = useRef<HTMLDivElement | null>(null)
  host.current ??= document.createElement('div')

  useEffect(() => {
    const node = host.current
    if (node === null) return
    node.setAttribute('data-overlay-passthrough', '')
    document.body.appendChild(node)
    return () => {
      node.remove()
    }
  }, [])

  // All the focus and pause bookkeeping, in one testable place — see the hook
  // for why it is not inline here any more.
  const { paused, handlers, focusOnDismiss } = useStackFocus(host.current, queue)

  // The F6 route to this queue and back — #216. Mounted here, once, because
  // this is the region that had no way in.
  useRegionRotor()

  const show = useCallback((request: ToastRequest) => {
    sequence += 1
    dispatch({ type: 'show', toast: { ...request, id: `toast-${sequence}` } })
  }, [])

  // Stable, and `Toast`'s timer depends on it — see that component's note on
  // why the id is an argument rather than a closure.
  //
  // NOT memoised on `[]` any more: `focusOnDismiss` closes over the host and the
  // hook's refs, so a stale `[]` closure would move focus using last render's
  // view of the stack. `Toast`'s timer effect depends on this identity, so it is
  // memoised on that function rather than left to churn every render.
  const dismiss = useCallback(
    (id: string, notFromPointer = false) => {
      focusOnDismiss(id, notFromPointer)
      dispatch({ type: 'dismiss', id })
    },
    [focusOnDismiss],
  )

  const api = useMemo(() => ({ show }), [show])

  // Derived during render, not held in a second piece of state. Two reducers
  // kept in step by an effect is how the counts drift.
  const polite = queue.filter((toast) => toast.kind !== 'error')
  const assertive = queue.filter((toast) => toast.kind === 'error')

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="toaster"
          role="region"
          aria-label="Notifications"
          // Focusable only while there is something here — the rotor treats a
          // declared tabindex as "this region is landable", and an empty queue
          // is not. Unconditional, this made F6 move focus into an invisible
          // empty strip on any page with no toast showing, which is most of
          // them. The role, the label and both live regions stay mounted from
          // the first render regardless, so nothing about announcement changes.
          tabIndex={queue.length > 0 ? -1 : undefined}
          {...handlers}
        >
          {/* Assertive FIRST, so a failure sits above a success rather than
              under one. Arrival order cannot hold across two regions — that is
              the price of one region per politeness level — so the order is at
              least a deliberate rule (severity) instead of a DOM accident.
              web-tester found a later `info` sitting above an earlier `error`.

              `aria-atomic="false"` on both, against the roles' own defaults.
              `role="status"` and `role="alert"` are implicitly atomic, so every
              arrival would re-announce the whole stack — which IS
              "announcements stack into noise", however few regions there are.

              `aria-relevant` is deliberately left unset: its default excludes
              `removals`, which is what keeps every auto-dismiss silent. Do not
              spell it out "for clarity". */}
          <div className="toaster__region" role="alert" aria-atomic="false">
            {assertive.map((toast) => (
              <Toast key={toast.id} {...toast} paused={paused} onDismiss={dismiss} />
            ))}
          </div>
          <div className="toaster__region" role="status" aria-atomic="false">
            {polite.map((toast) => (
              <Toast key={toast.id} {...toast} paused={paused} onDismiss={dismiss} />
            ))}
          </div>
        </div>,
        host.current,
      )}
    </ToastContext.Provider>
  )
}

/**
 * Raise a toast from anywhere below the provider.
 *
 * Throws without one, rather than handing back a no-op. A swallowed toast is
 * the worst of both worlds: the caller believes it reported the outcome and the
 * user is told nothing at all.
 */
export function useToast(): Api {
  const api = useContext(ToastContext)
  if (api === null) throw new Error('useToast must be used inside a <ToastProvider>')
  return api
}
